import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FeatureFlagsManager } from '../../../../../utils/core/feature-flags-manager';
import { isDefined } from '../../../../../utils/variables';
import { isSweref99CoordinateSystem } from '../../../../../viewer/coordinate-manager';
import { ViewSettings } from '../../../../../viewer/view-settings';
import { ProjectSpatialReferenceContext } from '../../../../state/project-reference-state';
import { reset } from '../../../../utils/styled-reset';
import { Icon } from '../../../icon/icon';
import { RightPanelHeader } from '../../../skyview/right-menu/components/right-menu-header';
import { Stack } from '../../../stack/stack';
import { OnDemandNotAvailablePanel } from './panels/on-demand-not-available-panel';
import { RealPropertyMenu } from './real-property-menu';
import { SkyviewExportMenu } from './skyview-export-menu';

type ActiveMenu = 'none' | 'realProperty' | 'skyviewExport';

export const OnDemandMenu = () => {
  const { t } = useTranslation();
  const { coordinateSystem } = React.useContext(ProjectSpatialReferenceContext);
  const coordinateSystemMissing = !isDefined(coordinateSystem);
  const isCoordinateSystemSupported = isSweref99CoordinateSystem(coordinateSystem?.name);
  const [activeMenu, setActiveMenu] = useState<ActiveMenu>('none');

  const showRealProperty = isCoordinateSystemSupported && !coordinateSystemMissing;
  const showSkyviewExport =
    FeatureFlagsManager.instance.isEnabled('SK-6609-skyview-download') &&
    !ViewSettings.instance.isCadViewer;

  if (!showRealProperty && !showSkyviewExport) {
    return renderWithHeader(t, <OnDemandNotAvailablePanel />);
  }

  const handleBack = () => {
    setActiveMenu('none');
  };

  const renderContent = () => {
    if (activeMenu === 'realProperty') {
      return <RealPropertyMenu />;
    }
    if (activeMenu === 'skyviewExport') {
      return <SkyviewExportMenu.Component />;
    }
    return (
      <ServiceSelectionContainer>
        {showRealProperty && (
          <ServiceSelectionItem
            icon={['fal', 'house-circle-check']}
            title={t('onDemand.propertyBoundary.title', { ns: 'components' })}
            onClick={() => setActiveMenu('realProperty')}
          />
        )}
        {showSkyviewExport && (
          <ServiceSelectionItem
            icon={['fal', 'cloud-download']}
            title={t('onDemand.skyviewExport.title', { ns: 'components' })}
            onClick={() => setActiveMenu('skyviewExport')}
          />
        )}
      </ServiceSelectionContainer>
    );
  };

  return renderWithHeader(t, renderContent(), activeMenu !== 'none', handleBack);
};

const renderWithHeader = (
  t: TFunction,
  children: React.ReactNode,
  showBack?: boolean,
  onBack?: () => void,
) => {
  return (
    <Component>
      <RightPanelHeader icon={['fas', 'rocket']} name="On Demand">
        {showBack && onBack && (
          <Icon
            icon={['fas', 'arrow-left']}
            style={{ marginRight: '0.5em', cursor: 'pointer' }}
            title={t('back', { ns: 'common' })}
            onClick={onBack}
          />
        )}
      </RightPanelHeader>
      {children}
    </Component>
  );
};

interface ServiceSelectionItemProps {
  icon: IconProp;
  title: string;
  onClick: () => void;
}

const ServiceSelectionItem = ({ icon, title, onClick }: ServiceSelectionItemProps) => {
  return (
    <StyledContainer alignItems="center" direction="column" spacing={0.5} onClick={onClick}>
      <StyledIconContainer>
        <Icon
          icon={icon}
          style={{ fontSize: '48px', width: '48px', height: '48px', flexShrink: 0 }}
        />
      </StyledIconContainer>
      <StyledTitle>{title}</StyledTitle>
    </StyledContainer>
  );
};

const Component = styled(Stack)`
  height: 100%;
`;

const ServiceSelectionContainer = styled.div`
  margin: 1em;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1em;
  align-items: stretch;

  > * {
    min-width: 0;
  }
`;

const StyledIconContainer = styled.div`
  width: 64px;
  height: 64px;
  min-width: 64px;
  min-height: 64px;
  flex-shrink: 0;
  border-radius: 0.5em;
  color: ${(props) => props.theme.color.gray.darkest};
  border: 1px solid ${(props) => props.theme.color.gray.lightest};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const StyledTitle = styled.span`
  ${reset}
  font-size: 14px;
  text-align: center;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
`;

const StyledContainer = styled(Stack)`
  cursor: pointer;
  border-radius: 5px;
  padding: 1em;
  transition: background-color 0.2s;
  height: 100%;
  width: 100%;
  min-width: 0;
  justify-content: space-between;
  box-sizing: border-box;

  &:hover {
    background-color: ${(props) => props.theme.color.gray.lightest};
  }
`;
